import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  Component,
  HostBinding,
  HostListener,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { NgpTextarea } from 'ng-primitives/textarea';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';
import { SuiFormControlBase } from '../shared/cva-helper';

@Component({
  selector: 'sui-textarea',
  standalone: true,
  imports: [NgpTextarea, FaIconComponent],
  templateUrl: './sui-textarea.component.html',
  styleUrl: './sui-textarea.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiTextareaComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiTextareaComponent),
    },
  ],
})
export class SuiTextareaComponent
  extends SuiFormControlBase<string>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-textarea-${++SuiTextareaComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'textarea';

  readonly faCircleExclamation = faCircleExclamation;

  @Input() placeholder = '';
  @Input() rows = 3;
  @Input() readonly = false;
  @Input() autocomplete = '';
  @Input() id = '';
  // Code / query text: the text-code recipe (12px monospace) instead of body text,
  // with the browser's spellcheck / autocorrect off - queries are not prose.
  @Input() code = false;
  // Fill: the control stretches to the height its flex-column parent gives it
  // (the textarea grows instead of keeping its `rows` height); manual resize off.
  @Input() @HostBinding('class.sui-textarea--fill') fill = false;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return !this.value();
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
  }

  onInput(event: Event): void {
    this.setValue((event.target as HTMLTextAreaElement).value);
  }
}
